import { FetchError, postJSON } from 'chairisher/util/fetch';
import { getProductDataUrl } from 'chairisher/context/product';

/**
 * Fetches data for given list of product IDS.
 *
 * @param {Array.<number|string>} productIds
 * @param {boolean=} isPurchase whether this request is purchase related, e.g. for mini cart.
 * @returns {Promise<Object>} Promise of an object mapping product ids to product data objects
 */
export async function fetchProducts(productIds, isPurchase = false) {
    try {
        const response = await postJSON(getProductDataUrl(), {
            product_ids: productIds,
            is_purchase: isPurchase,
        });
        return response?.products || {};
    } catch (error) {
        if (error instanceof FetchError) {
            return {};
        }
        throw error;
    }
}

/**
 * Fetches data for given product ID.
 *
 * @param {Number} productId
 * @returns {Promise<Object>} Promise of a product data object.
 */
export async function fetchProduct(productId) {
    const productMap = await fetchProducts([productId]);
    return productMap[productId];
}

/**
 * Transforms data into an object that the server will accept for a product update request.
 *
 * @param {Object} data The un-formatted data
 * @param {Object} metadata The product's metadata
 * @returns {Object} Formatted data that is ready to submit to the product update endpoint
 */
export function formatProductUpdateData(data, metadata) {
    const formattedData = {
        meta: { revision: metadata.revision },
        product: {},
    };

    // keys that are not present in metadata, but should
    // be inserted into it as a part of the request
    const extraMetaKeys = [
        'action',
        'declined_reason_codes',
        'declined_reason_note',
        'incomplete_reason_codes',
        'incomplete_reason_note',
        'should_save',
        'variant_attribute_group_code',
        'variant_parent_id',
    ];

    Object.entries(data).forEach(([key, value]) => {
        if (key in metadata || extraMetaKeys.includes(key)) {
            formattedData.meta[key] = value;
        } else if (Array.isArray(value) && !['brands', 'images', 'commission_details'].includes(key)) {
            formattedData.product[key] = { value: value.map((v) => ({ value: v })) };
        } else {
            formattedData.product[key] = { value };
        }
    });

    return formattedData;
}
